import { useState } from "react";
import { Link } from "react-router-dom";
import { heroImage } from "@/mocks/home";

const points = [
  "テキサス州のコミカレが中心",
  "エージェント未使用で出願",
  "4年制大学への編入を目指して準備中",
];

// ガイド販売ページが完成したら、この GUIDE_URL に URL を入れるだけで
// ボタンが購入ページへのリンクとして動作します。
const GUIDE_URL: string | null = null;

export default function Hero() {
  const [guideNoticeOpen, setGuideNoticeOpen] = useState(false);

  return (
    <section className="w-full bg-background-100">
      <div className="mx-auto w-full max-w-content px-5 pb-16 pt-12 md:px-6 md:pb-20 md:pt-16 lg:pb-24 lg:pt-20">
        <div className="grid items-center gap-10 lg:grid-cols-[45fr_55fr] lg:gap-14">
          <div className="w-full animate-rise">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary-700">
              <span className="h-[1px] w-6 bg-primary-500" />
              Study Abroad in the USA
              <span aria-hidden="true">🇺🇸</span>
            </p>

            <h1 className="mt-5 font-heading font-bold tracking-tight text-foreground-950">
              <span className="block text-[16px] font-semibold leading-[1.6] text-foreground-700 sm:text-[18px] md:text-[20px]">
                コミカレから4年制大学へ。
              </span>
              <span className="mt-3 block text-[27px] leading-[1.45] sm:text-[32px] md:text-[36px] lg:text-[40px] lg:leading-[1.4]">
                費用を抑えて、
                <br />
                <span className="text-primary-700">アメリカ留学を実現しよう。</span>
              </span>
            </h1>

            <p className="mt-6 max-w-xl text-[14px] leading-[1.95] text-foreground-700 md:text-[15px]">
              留学エージェントを使わずに渡米した現役コミュニティカレッジ生が、アメリカ留学の準備から大学生活、4年制大学への編入準備まで、実体験をもとに紹介します。
            </p>
            <p className="mt-3 max-w-xl text-[12px] leading-[1.9] text-foreground-600">
              当面は、テキサス州のコミュニティカレッジへの留学と、そこから4年制大学への編入を中心に扱います。
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href="https://note.com/mypace_diary"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-7 py-3.5 text-[14px] font-semibold text-background-50 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-accent-600 hover:shadow-md"
              >
                留学ブログを読む
                <i className="ri-arrow-right-line text-[15px]" />
              </a>
              <div className="relative">
                {GUIDE_URL ? (
                  <a
                    href={GUIDE_URL}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 border-primary-700 bg-background-50 px-7 py-3.5 text-[14px] font-semibold text-primary-800 transition-all hover:-translate-y-0.5 hover:bg-primary-50"
                  >
                    留学準備ガイドを見る
                    <i className="ri-arrow-right-line text-[15px]" />
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => setGuideNoticeOpen((prev) => !prev)}
                    aria-expanded={guideNoticeOpen}
                    className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 border-primary-700 bg-background-50 px-7 py-3.5 text-[14px] font-semibold text-primary-800 transition-all hover:-translate-y-0.5 hover:bg-primary-50 sm:w-auto"
                  >
                    留学準備ガイド（準備中）
                    <i className="ri-arrow-right-line text-[15px]" />
                  </button>
                )}

                {!GUIDE_URL && guideNoticeOpen && (
                  <div className="absolute left-0 top-full z-20 mt-2 w-[280px] rounded-lg border border-background-200 bg-background-50 px-3.5 py-3 text-[12px] leading-[1.8] text-foreground-700 shadow-[0_10px_24px_-16px_rgba(23,37,35,0.45)]">
                    現在、留学準備ガイドを制作中です。公開までしばらくお待ちください。
                    <Link
                      to="/guides"
                      className="mt-2 flex items-center gap-1 whitespace-nowrap text-[11.5px] font-semibold text-primary-700 transition-colors hover:text-primary-800"
                    >
                      準備中のガイド一覧を見る
                      <i className="ri-arrow-right-line text-[12px]" />
                    </Link>
                  </div>
                )}
              </div>
            </div>

            <ul className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2">
              {points.map((point) => (
                <li
                  key={point}
                  className="flex items-center gap-1.5 text-[12px] font-medium text-foreground-600"
                >
                  <i className="ri-check-line text-[14px] text-primary-600" />
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <div className="w-full animate-rise-slow delay-200">
            <div className="relative aspect-[3/2] w-full overflow-hidden rounded-xl bg-background-200 shadow-[0_18px_40px_-24px_rgba(23,37,35,0.35)]">
              <img
                src={heroImage}
                alt="赤レンガの校舎と芝生が広がるアメリカのコミュニティカレッジのキャンパス"
                title="アメリカ コミュニティカレッジ キャンパス 留学"
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-foreground-500">
              <span className="flex items-center gap-2">
                <i className="ri-map-pin-line text-[14px] text-primary-600" />
                Community College Campus, U.S.A.
              </span>
              <span className="flex items-center gap-1.5 text-foreground-400">
                <i className="ri-image-line text-[13px]" />
                ※イメージ画像
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}